import { inject, Injectable } from "@angular/core";
import { filter, first, from, map, switchMap } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { GraphqlMessageInterface } from "@app/kernel/graphql/interfaces/graphql-message.interface";
import { IVehicleBlock, IVehicleBlockSummary } from "../interfaces/vehicle-block.interface";
import { IVehicleBlockActionInput, IVehicleBlockCloseInput, IVehicleBlockRescheduleInput } from "../interfaces/vehicle-block-input.interface";
import { VEHICLE_BLOCK_FORM_QUERY } from "../queries/vehicle-block-form.query";
import { PopupService } from "@app/kernel/popups/popup/modules/confirmation-dialog/services/confirmation-popup/popup.service";

/**
 * API service for interacting with vehicleBlock-related GraphQL operations.
 *
 * Provides methods for retrieving, enabling, disabling, and deleting vehicle blocks.
 */
@Injectable()
export class VehicleBlockApiService {
  /**
   * GraphQL service used for executing queries and mutations.
   */
  private readonly graphService = inject(GraphService);

  private popupService = inject(PopupService);

  /** Attaches successfully staged files and returns the refreshed block. */
  addVehicleBlockFiles(id: string, files: string[]) {
    return this.graphService
      .constructMutation<{
        addVehicleBlockFiles: IVehicleBlock;
      }>("addVehicleBlockFiles", { id: "ID!", input: "VehicleBlockFilesInput!" }, { id, input: { files } }, [VEHICLE_BLOCK_FORM_QUERY])
      .pipe(
        first(),
        map(response => {
          if (response.errors?.length) throw response.errors[0];
          if (!response.data?.addVehicleBlockFiles) throw new Error("Missing attachment response");
          return response.data.addVehicleBlockFiles;
        })
      );
  }

  /** Unlinks an attached file after backend permission and membership checks. */
  removeVehicleBlockFile(id: string, key: string) {
    return this.graphService
      .constructMutation<{
        removeVehicleBlockFile: IVehicleBlock;
      }>("removeVehicleBlockFile", { id: "ID!", key: "String!" }, { id, key }, [VEHICLE_BLOCK_FORM_QUERY])
      .pipe(
        first(),
        map(response => {
          if (response.errors?.length) throw response.errors[0];
          if (!response.data?.removeVehicleBlockFile) throw new Error("Missing attachment response");
          return response.data.removeVehicleBlockFile;
        })
      );
  }

  /**
   * Fetches a vehicleBlock by its ID using the specified data shape.
   *
   * @param id - The ID of the vehicleBlock to retrieve.
   * @param dataShape - The shape of the GraphQL data to return. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable that emits the fetched `IVehicleBlock`.
   * @throws GraphQL error if the response contains errors.
   */
  getVehicleBlock(id: string, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    return this.graphService.constructQuery<{ getVehicleBlock: IVehicleBlock }>(dataShape, "getVehicleBlock", { id }).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.getVehicleBlock;
      })
    );
  }

  /**
   * Fetches the vehicleBlock counters displayed by the list page summary tabs.
   *
   * @returns An observable emitting the counter for each computed status tab.
   * @throws GraphQL error if the response contains errors.
   */
  getVehicleBlockSummary() {
    return this.graphService
      .constructQuery<{ getVehicleBlockSummary: IVehicleBlockSummary }>(
        `
          allCount
          scheduledCount
          inProgressCount
          cancelledCount
          closedCount
        `,
        "getVehicleBlockSummary"
      )
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getVehicleBlockSummary;
        })
      );
  }

  /**
   * Enables a vehicleBlock by its ID after confirmation.
   *
   * @param id - The ID of the vehicleBlock to enable.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the updated `IVehicleBlock`.
   */
  enableVehicleBlock(id: string, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    return from(this.popupService.confirm(this.getConfirmStatusOptions(true))).pipe(
      filter(confirmed => confirmed),
      switchMap(() => {
        const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
        return this.graphService.constructMutation<{ enableVehicleBlock: IVehicleBlock }>("enableVehicleBlock", { id: "ID!" }, { id }, shape).pipe(
          first(),
          map(data => {
            if (data.errors?.length) {
              throw data.errors[0];
            }
            return data.data?.enableVehicleBlock;
          })
        );
      })
    );
  }

  /**
   * Disables a vehicleBlock by its ID after confirmation.
   *
   * @param id - The ID of the vehicleBlock to disable.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the updated `IVehicleBlock`.
   */
  disableVehicleBlock(id: string, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    return from(this.popupService.confirm(this.getConfirmStatusOptions(false))).pipe(
      filter(confirmed => confirmed),
      switchMap(() => {
        const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
        return this.graphService.constructMutation<{ disableVehicleBlock: IVehicleBlock }>("disableVehicleBlock", { id: "ID!" }, { id }, shape).pipe(
          first(),
          map(data => {
            if (data.errors?.length) {
              throw data.errors[0];
            }
            return data.data?.disableVehicleBlock;
          })
        );
      })
    );
  }

  /**
   * Cancels a vehicleBlock. No further management actions remain available afterwards.
   *
   * @param id - The ID of the vehicleBlock to cancel.
   * @param input - Optional reason stored on the lifecycle history entry.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the cancelled `IVehicleBlock`.
   * @throws GraphQL error if the response contains errors.
   */
  cancelVehicleBlock(id: string, input: IVehicleBlockActionInput, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        cancelVehicleBlock: IVehicleBlock;
      }>("cancelVehicleBlock", { id: "ID!", input: "VehicleBlockActionInput" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.cancelVehicleBlock;
        })
      );
  }

  /**
   * Reopens a closed vehicleBlock.
   *
   * @param id - The ID of the vehicleBlock to reopen.
   * @param input - Optional reason stored on the lifecycle history entry.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the reopened `IVehicleBlock`.
   * @throws GraphQL error if the response contains errors.
   */
  reopenVehicleBlock(id: string, input: IVehicleBlockActionInput, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        reopenVehicleBlock: IVehicleBlock;
      }>("reopenVehicleBlock", { id: "ID!", input: "VehicleBlockActionInput" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.reopenVehicleBlock;
        })
      );
  }

  /**
   * Closes an in progress vehicleBlock, optionally appending further attachments.
   *
   * @param id - The ID of the vehicleBlock to close.
   * @param input - Staged file keys appended to the block while closing it.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the closed `IVehicleBlock`.
   * @throws GraphQL error if the response contains errors.
   */
  closeVehicleBlock(id: string, input: IVehicleBlockCloseInput, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        closeVehicleBlock: IVehicleBlock;
      }>("closeVehicleBlock", { id: "ID!", input: "CloseVehicleBlockInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.closeVehicleBlock;
        })
      );
  }

  /**
   * Moves the scheduling window of a vehicleBlock.
   *
   * @param id - The ID of the vehicleBlock to reschedule.
   * @param input - The replacement scheduling window.
   * @param dataShape - The shape of the response data. Defaults to `VEHICLE_BLOCK_FORM_QUERY`.
   * @returns An observable emitting the rescheduled `IVehicleBlock`.
   * @throws GraphQL error if the response contains errors.
   */
  rescheduleVehicleBlock(id: string, input: IVehicleBlockRescheduleInput, dataShape: string | string[] = VEHICLE_BLOCK_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        rescheduleVehicleBlock: IVehicleBlock;
      }>("rescheduleVehicleBlock", { id: "ID!", input: "RescheduleVehicleBlockInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.rescheduleVehicleBlock;
        })
      );
  }

  /**
   * Deletes a vehicleBlock by its ID.
   *
   * @param id - The ID of the vehicleBlock to delete.
   * @returns An observable emitting the GraphQL deletion response message.
   * @throws GraphQL error if the response contains errors.
   */
  deleteVehicleBlock(id: string) {
    return this.graphService
      .constructMutation<{ deleteVehicleBlock: GraphqlMessageInterface }>("deleteVehicleBlock", { id: "ID!" }, { id }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.deleteVehicleBlock;
        })
      );
  }

  /**
   * Returns the configuration for the enable/disable confirmation popup.
   *
   * @param enabled - Whether the target state is enabled (true) or disabled (false).
   * @returns The options for the confirmation dialog.
   */
  private getConfirmStatusOptions(enabled: boolean) {
    return {
      header: enabled ? "COMMON.CONFIRMATION.ENABLE_HEADER" : "COMMON.CONFIRMATION.DISABLE_HEADER",
      message: enabled ? "COMMON.CONFIRMATION.ENABLE_MESSAGE" : "COMMON.CONFIRMATION.DISABLE_MESSAGE",
      acceptLabel: enabled ? "COMMON.CONFIRMATION.ENABLE_ACCEPT_LABEL" : "COMMON.CONFIRMATION.DISABLE_ACCEPT_LABEL",
      rejectLabel: "COMMON.CONFIRMATION.REJECT_LABEL",
      acceptLabelColor: enabled ? "blue" : "red",
      width: "500px",
      height: "",
      outlined: true,
    };
  }
}
